import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../../../../redux/store";

// Import slice e types - PATH RELATIVI!
import {
    OrdiniLavoroList,
    fetchOrdiniLavoroByPrevisione,
    deleteOrdiniLavoro,
    saveOrdiniLavoro,
    setPrevisioneCorrenteId,
} from "./ordiniLavoroByPrevisioneSlice";

import {createOrdiniLavoroVuoto} from "../../../../../../api/pianificazione/ordiniLavoroApi";
import {ErrorStatusTypes} from "components/common/errorsDeclarations";
import {Previsione} from "../../previsioniSlice";

// Material UI
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";

// Icons
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InventoryIcon from "@mui/icons-material/Inventory";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";

// Components
import OrdiniLavoroInlineForm from "./OrdiniLavoroInlineForm";
import {convertToDateTime} from "components/common/dateUtils/convertToDateTime";
import {theme} from "../../../../../theme";
import {OrdineLavoro} from "../../../../produzione/ordiniLavoro/ordiniLavoroTypes";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {useAutorizzazioneGruppi} from "../../../../../authentication/useAutorizzazioneGruppi";
import {PermissionTypes} from "../../../../../authentication/permissionsGroups";
import LinearProgress from "@mui/material/LinearProgress";

// ============================================
// INTERFACES
// ============================================

interface OrdiniLavoroCardProps {
    previsione: Previsione;
    consentiModifica: boolean;
}

// ============================================
// COMPONENT
// ============================================

const OrdiniLavoroCard = ({previsione, consentiModifica}: OrdiniLavoroCardProps) => {
    const dispatch = useAppDispatch();

    // State locale per nuovo ordineLavoro
    const [isAddingNew, setIsAddingNew] = useState(false);

    // ============================================
    // SELECTORS
    // ============================================

    const ordiniLavoroList = useSelector<RootState, OrdiniLavoroList | undefined>(
        (state) => previsione.id
            ? state.ordiniLavoroByPrevisioneState.ordiniLavoroByPrevisione[previsione.id]
            : undefined
    );

    const ordiniLavoroStatus = ordiniLavoroList?.errorsStack?.status || ErrorStatusTypes.OK;
    const ordiniLavoro = ordiniLavoroList?.results || [];
    const consentiModificaPianificatori = useAutorizzazioneGruppi([PermissionTypes.PIANIFICATORI, PermissionTypes.COMMERCIALI, PermissionTypes.GESTORI_SISTEMA])
    // ============================================
    // EFFECTS
    // ============================================

    useEffect(() => {
        if (previsione.id && ['CAMP'].includes(previsione.fase_collezione || '')) {
            dispatch(setPrevisioneCorrenteId(previsione.id));
            dispatch(fetchOrdiniLavoroByPrevisione({previsioneId: previsione.id}));
        }
    }, [dispatch, previsione.id, previsione.fase_collezione]);

    // ============================================
    // HANDLERS
    // ============================================

    const handleAddNew = () => {
        if (!previsione.id) return;

        // Crea nuovo ordineLavoro vuoto
        const nuovoOrdiniLavoro = createOrdiniLavoroVuoto(previsione.id);

        // Salva subito per ottenere ID dal backend
        dispatch(saveOrdiniLavoro({
            previsioneId: previsione.id,
            ordineLavoroToSave: nuovoOrdiniLavoro
        })).then((result) => {
            if (result.meta.requestStatus === 'fulfilled') {
                const newOrdiniLavoro = (result.payload as any).data;
                setIsAddingNew(false);
            }
        });
    };


    // ============================================
    // UTILITY FUNCTIONS
    // ============================================


    // Calcola totali
    const totaleQtaPrevista = ordiniLavoro.reduce((sum, imp) =>
        sum + Number(imp.qta_prevista || 0), 0
    );
    const totaleQtaArrivata = ordiniLavoro.reduce((sum, imp) =>
        sum + Number(imp.qta_arrivata || 0), 0
    );

    // Non mostrare la card se lo stato non è appropriato
    if (!previsione.fase_collezione || !['CAMP'].includes(previsione.fase_collezione)) {
        return null;
    }

    // ============================================
    // RENDER
    // ============================================

    return (
        <Card sx={{
            mt: 2,
            borderLeft: `4px solid ${theme.palette.primary.main}`
        }}>
            <CardHeader
                avatar={<LocalShippingIcon/>}
                title={
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{alignItems: "center", justifyContent: "space-between"}}
                    >
                        <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
                            <Typography variant="h6">
                                Pianificazione Ordini di Lavoro
                            </Typography>

                            {ordiniLavoroStatus === ErrorStatusTypes.PENDING && (
                                <LinearProgress sx={{minWidth:200}} />
                            )}
                        </Stack>
                        <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
                            {/* Totali */}
                            <Chip
                                label={`Previsto: ${totaleQtaPrevista} pz`}
                                size="small"
                                variant="outlined"
                            />
                            <Chip
                                label={`Arrivato: ${totaleQtaArrivata} pz`}
                                size="small"
                                variant="outlined"
                                color={totaleQtaArrivata >= totaleQtaPrevista ? "info" : "warning"}
                            />
                            {/* Bottone aggiungi */}
                            {consentiModificaPianificatori && previsione.fase_collezione == 'CAMP' && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon/>}
                                    onClick={handleAddNew}
                                    disabled={isAddingNew}
                                >
                                    Nuovo Ordine di Lavoro
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                }
            />

            <CardContent>
                {ordiniLavoro.length > 0 && (
                    <>
                        <Grid container spacing={1} sx={{
                            mb: 1,
                            pb: 1,
                            px: 1,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            fontWeight: 'bold'
                        }}>
                            <Grid size={{xs: 0.4}}>
                                <Typography variant="caption" fontWeight="bold">ID</Typography>
                            </Grid>
                            <Grid size={{xs: 1.2}}>
                                <Typography variant="caption" fontWeight="bold">Stato</Typography>
                            </Grid>
                            <Grid size={{xs: 0.6}}>
                                <Typography variant="caption" fontWeight="bold">Q.tà Prev.</Typography>
                            </Grid>
                            <Grid size={{xs: 1.2}}>
                                <Typography variant="caption" fontWeight="bold">Arrivo Previsto</Typography>
                            </Grid>
                            <Grid size={{xs: 1}}>
                                <Typography variant="caption" fontWeight="bold">Ritardo</Typography>
                            </Grid>
                            <Grid size={{xs: 1.2}}>
                                <Typography variant="caption" fontWeight="bold">Arrivo Effettivo</Typography>
                            </Grid>
                            <Grid size={{xs: 1}}>
                                <Typography variant="caption" fontWeight="bold">Q.tà Arr.</Typography>
                            </Grid>
                            <Grid size={{xs: 1.2}}>
                                <Typography variant="caption" fontWeight="bold">Consegna Prevista</Typography>
                            </Grid>
                            <Grid size={{xs: 3.4}}>
                                <Typography variant="caption" fontWeight="bold">Note</Typography>
                            </Grid>
                            <Grid size={{xs: 0.8}}>
                                <Typography variant="caption" fontWeight="bold">Azioni</Typography>
                            </Grid>
                        </Grid>

                        {/* Righe ordiniLavoro */}
                        {ordiniLavoro.map((ordineLavoro) => (
                            <Box key={ordineLavoro.id}>
                                <OrdiniLavoroInlineForm
                                    ordineLavoro={ordineLavoro}
                                    previsioneId={previsione.id!}
                                    consentiModifica={consentiModifica}
                                />
                            </Box>
                        ))}

                    </>
                )}

                {/* Empty state */}
                {ordiniLavoro.length === 0 && ordiniLavoroStatus !== ErrorStatusTypes.PENDING && (
                    <Box sx={{textAlign: 'center', py: 3}}>
                        <InventoryIcon sx={{fontSize: 48, color: 'grey.400', mb: 1}}/>
                        <Typography variant="body2" color="text.secondary">
                            Nessun ordineLavoro pianificato
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default OrdiniLavoroCard;